import { makeAutoObservable, runInAction } from 'mobx'
import { ApiError, toApiError } from '@/api/errors'
import type { ListQuery } from '@/api/query'
import type { ListResponse } from '@/types/api'
import { readCache, writeCache } from './queryCache'

type Fetcher<T> = (query: ListQuery, options: { signal: AbortSignal }) => Promise<ListResponse<T>>

/** Lists on screen now: loaded and not disposed. «Обновить» in the top bar reloads them. */
const live = new Set<ListStore<unknown>>()

/**
 * Data half of a list workbench. Query state lives in the URL (see
 * useListQuery); this store only fetches, cancels stale requests and keeps the
 * previous page visible while the next one loads.
 *
 * With a `cacheKey` it is stale-while-revalidate: a query answered before
 * (this visit or an earlier one) shows its cached rows at once, marked as
 * refreshing, while the request for fresh values runs.
 */
export class ListStore<T> {
  items: T[] = []
  count = 0
  loading = false
  loaded = false
  error: ApiError | null = null

  private controller: AbortController | null = null
  private lastQuery: ListQuery = {}
  private readonly fetcher: Fetcher<T>
  private readonly cacheKey?: () => string
  private shownKey: string | null = null

  /** `cacheKey` names the list (e.g. the page id); it is read on every load, so a page may switch lists. */
  constructor(fetcher: Fetcher<T>, cacheKey?: () => string) {
    this.fetcher = fetcher
    this.cacheKey = cacheKey
    makeAutoObservable<ListStore<T>, 'controller' | 'lastQuery' | 'fetcher' | 'cacheKey' | 'shownKey'>(this, {
      controller: false,
      lastQuery: false,
      fetcher: false,
      cacheKey: false,
      shownKey: false,
    })
  }

  get isEmpty() {
    return this.loaded && !this.error && this.items.length === 0
  }

  /** Reloads every list on screen with its last query (the top bar «Обновить»). */
  static reloadLive() {
    for (const store of live) void store.reload()
  }

  async load(query: ListQuery) {
    live.add(this as ListStore<unknown>)
    this.controller?.abort()
    const controller = new AbortController()
    this.controller = controller
    this.lastQuery = query
    this.loading = true
    this.error = null

    const key = this.cacheKey ? `${this.cacheKey()}::${JSON.stringify(query)}` : null
    const cached = key ? readCache<ListResponse<T>>(key) : undefined
    // Show the last known answer for this exact query right away; the request below refreshes it.
    if (cached && key !== this.shownKey) {
      this.items = cached.items
      this.count = cached.count
      this.loaded = true
    }
    this.shownKey = key

    try {
      const result = await this.fetcher(query, { signal: controller.signal })
      if (key) writeCache(key, result)
      runInAction(() => {
        this.items = result.items
        this.count = result.count
        this.loaded = true
      })
    } catch (error) {
      const apiError = toApiError(error)
      if (apiError.isCancelled) return
      runInAction(() => {
        this.error = apiError
        this.loaded = true
      })
    } finally {
      if (this.controller === controller) {
        runInAction(() => {
          this.loading = false
        })
      }
    }
  }

  reload() {
    return this.load(this.lastQuery)
  }

  dispose() {
    live.delete(this as ListStore<unknown>)
    this.controller?.abort()
  }
}
